import React, { useState, useEffect } from "react";
import {
  Edit,
  SimpleForm,
  TextInput,
  NumberInput,
  BooleanInput,
  ImageInput,
  ImageField,
  required,
  useRecordContext,
  useNotify,
} from "react-admin";
import { Box, Button, IconButton, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";
import { shopImageApi } from "../../dataProvider/resources/shop";
import { ShopItemImage } from "../types";

function GalleryManager() {
  const record = useRecordContext();
  const notify = useNotify();
  const [images, setImages] = useState<ShopItemImage[]>([]);

  useEffect(() => {
    setImages(record?.images ?? []);
  }, [record?.images]);

  if (!record || images.length === 0) return null;

  const itemId = Number(record.id);

  const handleDelete = async (imageId: number) => {
    try {
      await shopImageApi.delete(itemId, imageId);
      setImages((prev) => prev.filter((img) => img.id !== imageId));
      notify("Bild gelöscht");
    } catch {
      notify("Fehler beim Löschen", { type: "error" });
    }
  };

  const handleMove = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= images.length) return;
    const reordered = [...images];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setImages(reordered);
    try {
      await shopImageApi.reorder(
        itemId,
        reordered.map((img, i) => ({ id: img.id, sort_order: i }))
      );
    } catch {
      setImages(images);
      notify("Fehler beim Sortieren", { type: "error" });
    }
  };

  return (
    <Box mb={2}>
      <Typography variant="subtitle2" mb={1}>
        Bilder
      </Typography>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        {images.map((img, index) => (
          <Box key={img.id} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
            <Box
              component="img"
              src={img.url}
              alt=""
              sx={{ width: 120, height: 90, objectFit: "cover", borderRadius: 1, border: "1px solid", borderColor: "divider" }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
              <IconButton size="small" disabled={index === 0} onClick={() => handleMove(index, -1)}>
                <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => handleDelete(img.id)}>
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" disabled={index === images.length - 1} onClick={() => handleMove(index, 1)}>
                <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function ShopItemEdit() {
  const navigate = useNavigate();

  return (
    <Box p={2}>
      <Box mb={2}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/shop-items")}>
          Zurück zur Artikelliste
        </Button>
      </Box>
      <Edit title="Artikel bearbeiten">
        <SimpleForm>
          <TextInput source="name" label="Name" validate={required()} fullWidth />
          <TextInput
            source="description"
            label="Beschreibung (optional)"
            fullWidth
            multiline
            rows={2}
          />
          <NumberInput
            source="price"
            label="Preis (€)"
            validate={required()}
            min={0}
            step={0.01}
            fullWidth
          />
          <TextInput
            source="sizes_text"
            label="Größen (kommagetrennt, z.B.: XS, S, M, L, XL)"
            fullWidth
            helperText="Leer lassen wenn keine Größenauswahl nötig"
          />
          <BooleanInput source="has_imprint" label="Aufdruck" />
          <BooleanInput source="active" label="Aktiv (im Shop sichtbar)" />
          <GalleryManager />
          <ImageInput source="images_new" label="Bilder hinzufügen" accept={{ "image/*": [] }} multiple>
            <ImageField source="src" title="title" />
          </ImageInput>
        </SimpleForm>
      </Edit>
    </Box>
  );
}
